import { Apollo, APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink, Options } from 'apollo-angular/http';
import {
  ApplicationConfig,
  inject,
  makeStateKey,
  Optional,
  PLATFORM_ID,
} from '@angular/core';
import {
  ApolloCache,
  ApolloClientOptions,
  ApolloLink,
  DefaultOptions,
  InMemoryCache,
  Observable,
} from '@apollo/client/core';
import { TransferState } from '@angular/core';
import { environment } from '../environments/environment';
import possibleTypesResult from './graphql/generated/fragment-matcher';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpHeaders } from '@angular/common/http';
import { ManagedFilterValue } from './graphql/generated/generated-types';
import { AuthService } from './shared/services/auth.service';


const STATE_KEY = makeStateKey<any>('apollo.state');
let apolloCache: InMemoryCache;

const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
};

export const graphqlProvider: ApplicationConfig['providers'] = [
  Apollo,
  {
    provide: APOLLO_OPTIONS,
    useFactory: apolloOptionsFactory,
    deps: [HttpLink, PLATFORM_ID, TransferState],
    //     deps: [HttpLink, PLATFORM_ID, TransferState, [new Optional(), REQUEST]],
  },
];

function mergeFields(existing: any, incoming: any) {
  return { ...existing, ...incoming };
}

function mergeManagedFilterValues(
  existing: ManagedFilterValue[] | undefined,
  incoming: ManagedFilterValue[] | undefined
): ManagedFilterValue[] {
  // Combine existing and incoming values based on unique identifier (e.g., id)
  const mergedValues: ManagedFilterValue[] = [];
  // Handle undefined or empty existing:
  const existingValueIds = existing?.map((value) => value.id) || [];

  // Handle undefined or empty incoming:
  if (!incoming || incoming.length === 0) {
    // If incoming is empty or undefined, return existing values (if any)
    return incoming || [];
  }

  incoming.forEach((incomingValue) => {
    const existingIndex = existingValueIds.indexOf(incomingValue.id);

    if (existingIndex !== -1) {
      // Update existing value if found by ID (safely accessing existing)
      mergedValues.push({ ...existing?.[existingIndex], ...incomingValue });
    } else {
      // Add new value if not found
      mergedValues.push(incomingValue);
    }
  });

  return mergedValues;
}

function relaceFields(existing: any, incoming: any) {
  return incoming;
}

// Trying to debug why sessions won't work in Safari 13.1
// but only on the live prod version.
function logInterceptorData(on: boolean) {
  localStorage.setItem('_logInterceptorData', on ? 'true' : 'false');
}

if (typeof window !== 'undefined') {
  (window as any).logInterceptorData = logInterceptorData;
}

export function apolloOptionsFactory(): ApolloClientOptions<any> {
  const platformId = inject(PLATFORM_ID);
  const httpLink = inject(HttpLink);
  const transferState = inject(TransferState);
  const AUTH_TOKEN_KEY = 'auth_token';
  const authService = inject(AuthService);

  apolloCache = new InMemoryCache({
    possibleTypes: possibleTypesResult.possibleTypes,
    typePolicies: {
      Query: {
        fields: {
          eligibleShippingMethods: {
            merge: relaceFields,
          },
          activeCustomer: {
            merge: relaceFields,
          },
        },
      },
      ManagedFilter: {
        fields: {
          values: {
            merge: relaceFields, // Custom merge function here
          },
        },
      },
      Product: {
        fields: {
          customFields: {
            merge: mergeFields,
          },
        },
      },
      Collection: {
        fields: {
          customFields: {
            merge: mergeFields,
          },
        },
      },
      Order: {
        fields: {
          lines: {
            merge: relaceFields,
          },
          shippingLines: {
            merge: relaceFields,
          },
          discounts: {
            merge: relaceFields,
          },
          shippingAddress: {
            merge: relaceFields,
          },
          billingAddress: {
            merge: relaceFields,
          },
        },
      },
      Customer: {
        fields: {
          addresses: {
            merge: relaceFields,
          },
          customFields: {
            merge: mergeFields,
          },
        },
      },
    },
  });

  let uri;
  if (isPlatformBrowser(platformId)) {
    if (
      !environment.local &&
      !environment.docker &&
      !environment.localProduction
    ) {
      const { browserApiPort, shopApiPath } = environment;
      uri = `${window.location.protocol}//${window.location.hostname}/${shopApiPath}`;
    } else if (environment.docker) {
      const { serverApiHost, shopApiPath } = environment;
      uri = `${serverApiHost}/${shopApiPath}`;
    } else if (environment.localProduction) {
      const { serverApiHost, shopApiPath } = environment;
      uri = `${serverApiHost}/${shopApiPath}`;
    } else {
      const { browserApiHost, serverApiPort, shopApiPath } = environment;
      uri = `${browserApiHost}:${serverApiPort}/${shopApiPath}`;
    }
  } else {
    const { serverApiHost, serverApiPort, shopApiPath } = environment;
    uri = `${serverApiHost}:${serverApiPort}/${shopApiPath}`;
  }
  const options: Options = {
    uri,
    withCredentials: false,
  };

  const http = httpLink.create(options);
  const metricsLink = new ApolloLink((operation, forward) => {
    const { operationName } = operation;
    const startTime = new Date().getTime();
    const observable = forward(operation);

    return new Observable((observer) => {
      observable.subscribe({
        next: (result) => {
          const elapsed = new Date().getTime() - startTime;

        //  if (isPlatformServer(platformId)) {
          //  console.warn(
          //    `SERVER : [METRICS][${operationName}] (${elapsed})ms  response`
         //   );
       //   }

        //  if (isPlatformBrowser(platformId) && !environment.production) {
         //   console.warn(
          //    `BROWSER : [METRICS][${operationName}] (${elapsed})ms  response`
          //  );
         // }

          observer.next(result);
        },
        error: (error) => {
          observer.error(error);
        },
        complete: () => {
          observer.complete();
        },
      });
    });
  });

 

  const middleware = new ApolloLink((operation, forward) => {
  const headers: { [key: string]: string } = {
    'Authorization': `Bearer ${isPlatformBrowser(platformId) ? localStorage.getItem(AUTH_TOKEN_KEY) : authService.getAuthToken() || null}`,
  };

  operation.setContext({
    headers: new HttpHeaders(headers)
  });

  return forward(operation);
  });

  const afterware = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const {
        response: { headers },
      } = operation.getContext(); // Destructure the response headers from the context
      const authHeader = headers.get('vendure-auth-token');
      if (authHeader && isPlatformBrowser(platformId)) {
        // If the auth token has been returned by the Vendure
        // server, we store it in localStorage
        localStorage.setItem(AUTH_TOKEN_KEY, authHeader);
      }

      if (authHeader && isPlatformServer(platformId)) {
        authService.setAuthToken(authHeader);
      }
      return response;
    });
  });

  const isBrowser = transferState.hasKey(STATE_KEY);
  if (isBrowser) {
    const state = transferState.get(STATE_KEY, null);
    if (isPlatformBrowser(platformId)) {
      // console.log('STATE OBJECT', state)
    }
    apolloCache.restore(state);
  } else {
    transferState.onSerialize(STATE_KEY, () => {
      return apolloCache.extract();
    });
    // Reset apolloCache after extraction to avoid sharing between requests
    apolloCache.reset();
  }

  return {
    cache: apolloCache,
    ssrMode: true,
    ssrForceFetchDelay: 500,
    link: ApolloLink.from([middleware, afterware, metricsLink, http]),
    // defaultOptions: defaultOptions
  };
}
